import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function RLCCircuitCalculator() {
  const [resistance, setResistance] = useState("");
  const [inductance, setInductance] = useState("");
  const [capacitance, setCapacitance] = useState("");
  const [frequency, setFrequency] = useState("");
  const [results, setResults] = useState<any>(null);

  const calculateRLC = () => {
    const r = parseFloat(resistance);
    const l = parseFloat(inductance);
    const c = parseFloat(capacitance);
    const f = parseFloat(frequency);

    if (isNaN(r) || isNaN(l) || isNaN(c) || isNaN(f)) {
      toast.error("Please enter valid values");
      return;
    }

    const omega = 2 * Math.PI * f;
    const xl = omega * l;
    const xc = 1 / (omega * c);
    const z = Math.sqrt(r * r + Math.pow(xl - xc, 2));
    const resonantFreq = 1 / (2 * Math.PI * Math.sqrt(l * c));
    const q = (omega * l) / r;

    setResults({
      xl,
      xc,
      impedance: z,
      resonantFreq,
      qualityFactor: q,
    });
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setResistance("");
    setInductance("");
    setCapacitance("");
    setFrequency("");
    setResults(null);
  };

  return (
    <PageLayout calculatorName="RLC Circuit Calculator" calculatorPath="/calculator/rlc-circuit">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">RC/RL/RLC Circuit Calculator</h1>
          <p className="text-muted-foreground">
            Analyze AC circuits with resistance, inductance, and capacitance
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>RLC Circuit Analysis</CardTitle>
            <CardDescription>
              Z = √(R² + (X_L - X_C)²) | f₀ = 1/(2π√LC)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="resistance">Resistance R (Ω)</Label>
                <Input
                  id="resistance"
                  type="number"
                  step="any"
                  placeholder="Enter resistance"
                  value={resistance}
                  onChange={(e) => setResistance(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inductance">Inductance L (H)</Label>
                <Input
                  id="inductance"
                  type="number"
                  step="any"
                  placeholder="Enter inductance (e.g., 0.001)"
                  value={inductance}
                  onChange={(e) => setInductance(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">1 mH = 0.001 H</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="capacitance">Capacitance C (F)</Label>
                <Input
                  id="capacitance"
                  type="number"
                  step="any"
                  placeholder="Enter capacitance (e.g., 1e-6)"
                  value={capacitance}
                  onChange={(e) => setCapacitance(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">1 µF = 1×10⁻⁶ F</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="frequency">Operating Frequency (Hz)</Label>
                <Input
                  id="frequency"
                  type="number"
                  step="any"
                  placeholder="Enter frequency"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateRLC} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {results && (
              <>
                <div className="space-y-3">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Inductive Reactance</p>
                          <p className="text-lg font-bold text-primary">{results.xl.toFixed(2)} Ω</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Capacitive Reactance</p>
                          <p className="text-lg font-bold text-primary">{results.xc.toFixed(2)} Ω</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Impedance</p>
                          <p className="text-lg font-bold text-primary">{results.impedance.toFixed(2)} Ω</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Quality Factor</p>
                          <p className="text-lg font-bold text-primary">{results.qualityFactor.toFixed(2)}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm text-muted-foreground">Resonant Frequency</p>
                          <p className="text-lg font-bold text-primary">{results.resonantFreq.toFixed(2)} Hz</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["rlc-circuit"]} trigger={results.impedance} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• X_L = ωL (inductive reactance increases with frequency)</p>
                <p>• X_C = 1/(ωC) (capacitive reactance decreases with frequency)</p>
                <p>• At resonance: X_L = X_C, minimum impedance Z = R</p>
                <p>• Quality factor Q measures energy storage vs. dissipation</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
